import { z } from "zod";

/**
 * recordingStatus - the pure client model for the server's recording state (D-09) and the
 * consent question (D-05). Kept free of React and of any HTTP so both the default room and the
 * new-design rooms share exactly one mapping from "what the server says" to "what this
 * participant is told and may do".
 *
 * Two pieces:
 * - recordingStatusSchema validates a server view before any of it is used (T-06.1-40). It is the
 *   same shape cl.state carries and the consent endpoints return, so cl.state's room state can
 *   embed it without a second model.
 * - consentUi turns the validated state plus the local identity into the message and the offered
 *   actions. Capture is shown as active only once the server says starting, recording or stopping
 *   (D-05): the client never guesses capture from an intent.
 *
 * tokenCanPublish decodes this client's own token to learn whether publish is withheld (the D-06
 * late-joiner hold). The SFU enforces the real rule; this only avoids a publish that would fail.
 * It never throws: a malformed token is treated as "publish allowed".
 */

export const RECORDING_PHASES = [
  "off",
  "awaiting_ack",
  "starting",
  "recording",
  "stopping",
  "saved",
  "failed",
] as const;

export type RecordingPhase = (typeof RECORDING_PHASES)[number];

/** Per-identity fields carry LiveKit identities only (never names or any org-private data). */
const identitySchema = z.string().min(1).max(200);

export const recordingStatusSchema = z.object({
  phase: z.enum(RECORDING_PHASES),
  requested: z.boolean(),
  segmentIndex: z.number().int().nullable(),
  acceptedIdentities: z.array(identitySchema).max(50),
  refusedIdentities: z.array(identitySchema).max(50),
  pendingIdentities: z.array(identitySchema).max(50),
  recordingSinceEpochMs: z.number().int().nonnegative().nullable(),
});

export type RecordingStatus = z.infer<typeof recordingStatusSchema>;

export type ConsentRole = "worker" | "customer";

export type ConsentUiKind =
  | "hidden"
  | "ask"
  | "waiting-for-others"
  | "declined-by-me"
  | "declined-by-someone"
  | "starting"
  | "recording"
  | "stopping"
  | "saved"
  | "failed";

export type ConsentUi = {
  kind: ConsentUiKind;
  /** True only while the server confirms capture (starting, recording, stopping). */
  capturing: boolean;
  canAccept: boolean;
  canDecline: boolean;
  message: string;
};

const MESSAGES = {
  workerAsk: "Record this session? Recording starts only when everyone in the call agrees.",
  customerAsk:
    "Support would like to record this call. Recording starts only if everyone agrees, and the call continues either way.",
  waiting: "Waiting for everyone to agree before recording starts.",
  declinedByMe: "You chose not to record. The call continues without recording.",
  declinedBySomeone: "Recording is off because not everyone agreed.",
  starting: "Recording is starting...",
  recording: "This call is being recorded",
  stopping: "Saving the recording...",
  saved: "The recording was saved.",
  failed: "The recording stopped because of a technical problem. The call continues.",
} as const;

const CAPTURING_PHASES: ReadonlySet<string> = new Set(["starting", "recording", "stopping"]);

function hiddenUi(): ConsentUi {
  return { kind: "hidden", capturing: false, canAccept: false, canDecline: false, message: "" };
}

/**
 * Maps the server's recording state and this participant's identity to what they see and may do.
 *
 * Precedence: a refusal by me first, then an outstanding ask to me, then the phase. "Me pending"
 * wins over everything else in the phase because a held or not-yet-answered participant must
 * still be asked (D-05, D-06) - including a late joiner arriving while the phase is already
 * "recording".
 */
export function consentUi(
  status: RecordingStatus | null,
  localIdentity: string,
  role: ConsentRole,
): ConsentUi {
  if (status === null || !status.requested) {
    return hiddenUi();
  }

  const capturing = CAPTURING_PHASES.has(status.phase);

  if (status.refusedIdentities.includes(localIdentity)) {
    return {
      kind: "declined-by-me",
      capturing,
      canAccept: true,
      canDecline: false,
      message: MESSAGES.declinedByMe,
    };
  }

  if (status.pendingIdentities.includes(localIdentity)) {
    return {
      kind: "ask",
      capturing,
      canAccept: true,
      canDecline: true,
      message: role === "worker" ? MESSAGES.workerAsk : MESSAGES.customerAsk,
    };
  }

  switch (status.phase) {
    case "starting":
      return {
        kind: "starting",
        capturing,
        canAccept: false,
        canDecline: true,
        message: MESSAGES.starting,
      };
    case "recording":
      return {
        kind: "recording",
        capturing,
        canAccept: false,
        canDecline: true,
        message: MESSAGES.recording,
      };
    case "stopping":
      return {
        kind: "stopping",
        capturing,
        canAccept: false,
        canDecline: true,
        message: MESSAGES.stopping,
      };
    case "saved":
      return {
        kind: "saved",
        capturing,
        canAccept: false,
        canDecline: false,
        message: MESSAGES.saved,
      };
    case "failed":
      return {
        kind: "failed",
        capturing,
        canAccept: false,
        canDecline: false,
        message: MESSAGES.failed,
      };
    case "awaiting_ack":
      if (status.refusedIdentities.length > 0) {
        return {
          kind: "declined-by-someone",
          capturing,
          canAccept: false,
          canDecline: false,
          message: MESSAGES.declinedBySomeone,
        };
      }
      return {
        kind: "waiting-for-others",
        capturing,
        canAccept: false,
        canDecline: true,
        message: MESSAGES.waiting,
      };
    case "off":
    default:
      if (status.pendingIdentities.length > 0) {
        return {
          kind: "waiting-for-others",
          capturing,
          canAccept: false,
          canDecline: true,
          message: MESSAGES.waiting,
        };
      }
      return hiddenUi();
  }
}

function base64UrlToBytes(value: string): Uint8Array | null {
  const normalised = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalised + "=".repeat((4 - (normalised.length % 4)) % 4);
  try {
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}

/**
 * D-06: true when this token withholds publishing (video.canPublish is false). Anything
 * unreadable - a garbage, two-part or non-JSON token - is treated as "publish allowed" so the
 * client never blocks a participant on a decode it could not make. Never throws.
 */
export function tokenCanPublish(jwt: string): boolean {
  const parts = jwt.split(".");
  if (parts.length !== 3) {
    return true;
  }
  const bytes = base64UrlToBytes(parts[1]);
  if (bytes === null) {
    return true;
  }
  let decoded: string;
  try {
    decoded = new TextDecoder().decode(bytes);
  } catch {
    return true;
  }
  try {
    const payload = JSON.parse(decoded) as { video?: { canPublish?: unknown } } | null;
    return payload?.video?.canPublish !== false;
  } catch {
    return true;
  }
}
